import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Alert,
  Button,
  Skeleton,
  Badge,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Announcement,
  NewReleases,
  Info,
  Warning,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Article,
  FiberNew,
  Update,
  Gavel,
  MarkEmailRead,
  Refresh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';

interface NewsItem {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'feature' | 'update' | 'warning' | 'terms';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  is_read: boolean;
  read_at?: string;
  expires_at?: string;
  extra_data?: {
    version?: string;
    link?: string;
  };
}

const PlatformNews: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);

  useEffect(() => {
    fetchNews();
    fetchUnreadCount();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform-news', {
        params: { limit: 5 }
      });
      setNews(response.data);
    } catch (err) {
      console.error('Failed to fetch platform news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/platform-news/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const markAsRead = async (newsId: string) => {
    try {
      await api.post(`/platform-news/${newsId}/mark-read`);
      setNews(prev => prev.map(item => 
        item.id === newsId ? { ...item, is_read: true } : item
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/platform-news/mark-all-read');
      setNews(prev => prev.map(item => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <FiberNew color="primary" />;
      case 'update':
        return <Update color="info" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'terms':
        return <Gavel color="error" />;
      default:
        return <Info color="action" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'feature':
        return 'New Feature';
      case 'update':
        return 'Update';
      case 'warning':
        return 'Warning';
      case 'terms':
        return 'Terms Update';
      default:
        return 'Information';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
          <Skeleton variant="rectangular" height={100} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button size="small" onClick={fetchNews}>Retry</Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Card sx={{ position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Announcement color="primary" />
            <Typography variant="h6" component="h2">
              Platform News
            </Typography>
            {unreadCount > 0 && (
              <Badge badgeContent={unreadCount} color="error">
                <NewReleases fontSize="small" />
              </Badge>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" onClick={markAllAsRead}>
                  <MarkEmailRead fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => {
                fetchNews();
                fetchUnreadCount();
              }}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {news.length === 0 ? (
            <Alert severity="info" icon={<Info />}>
              No news at the moment. Check back later for updates!
            </Alert>
          ) : (
            <List sx={{ p: 0 }}>
              {news.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 1.5,
                      cursor: 'pointer',
                      backgroundColor: !item.is_read ? 'action.hover' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                      borderRadius: 1,
                    }}
                    onClick={() => {
                      setSelectedNews(item);
                      if (!item.is_read) {
                        markAsRead(item.id);
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getIcon(item.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: !item.is_read ? 600 : 400 }}>
                            {item.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Chip
                              label={getTypeLabel(item.type)}
                              size="small"
                              color={item.type === 'warning' ? 'warning' : 'primary'}
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                            {item.priority !== 'normal' && (
                              <Chip
                                label={item.priority}
                                size="small"
                                color={getPriorityColor(item.priority) as any}
                                sx={{ height: 20 }}
                              />
                            )}
                            {!item.is_read && (
                              <Chip
                                label="NEW"
                                size="small"
                                color="error"
                                sx={{ height: 20 }}
                              />
                            )}
                          </Stack>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {item.content}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                            {format(new Date(item.created_at), 'MMM dd, yyyy • HH:mm')}
                            {item.extra_data?.version && ` • v${item.extra_data.version}`}
                          </Typography>
                        </>
                      }
                      secondaryTypographyProps={{
                        component: 'div'
                      }}
                    />
                    {item.is_read && (
                      <Tooltip title="Read">
                        <CheckCircle color="success" fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </ListItem>
                  {index < news.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Collapse>
      </CardContent>

      {/* Selected News Detail Modal/Expansion */}
      {selectedNews && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', backgroundColor: 'background.default' }}>
          <Typography variant="h6" gutterBottom>
            {selectedNews.title}
          </Typography>
          <Typography variant="body2" paragraph>
            {selectedNews.content}
          </Typography>
          {selectedNews.extra_data?.link && (
            <Button
              size="small"
              startIcon={<Article />}
              href={selectedNews.extra_data.link}
              target="_blank"
            >
              Learn More
            </Button>
          )}
          <Button size="small" onClick={() => setSelectedNews(null)}>
            Close
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default PlatformNews;